import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useMemo, useCallback } from "react";

import type * as Api from "@skilitics/billing-api-client/definition";
import getAuthClient from "@skilitics/designer-auth";

import { api } from "#api";

import { getPlan, unknownPlanName } from "./planMetadata.ts";

const queryKey = ["billing", "account"];

export const useRefreshAccess = () => {
  const client = useQueryClient();
  return useCallback(async () => {
    await getAuthClient().refreshApiToken();
    // This is used when access has changed, which can theoretically include
    // all responses like the module list responses' "access denied".
    // Simply refetch everything.
    await client.refetchQueries();
  }, [client]);
};

export const useAccount = (): UseQueryResult<Api.Account> => {
  return useQuery({
    queryKey,
    queryFn: () => api.get("/account"),
  });
};

export const useHasStripeAccount = () => {
  const account = useAccount();
  return !!account.data?.stripe;
};

// We can add more products in the future
export const useInteractProduct = () => {
  const account = useAccount();
  return useMemo(() => account.data?.products.find((item) => item.productKey === "interact"), [account.data]);
};

export const useCurrentPlanName = () => {
  const product = useInteractProduct();

  // Missing interact product means the server doesn't even know about interact, which
  // presumably means the UX is out of date and can't show the actual plan.
  // Showing anything other than "Unknown" would be misleading.
  if (!product) {
    return unknownPlanName;
  }

  const planKey = product.subscription ? product.subscription.planKey : null;
  const plan = getPlan(planKey);
  return plan?.name ?? unknownPlanName;
};

export const useCurrentPlanKey = () => {
  const product = useInteractProduct();

  return product?.subscription?.planKey;
};
